import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { supabase } from "../supabaseClient"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { getActiveYear, setActiveYear, getAllYears, createYear } from "../utils/activeYear"

interface YearData {
  year: number
  is_active: boolean
  entry_fee: number
  entries_open: boolean
}

interface Stats {
  totalEntries: number
  paidEntries: number
  teamsCount: number
  gamesCompleted: number
}

export default function Admin() {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)
  const [years, setYears] = useState<YearData[]>([])
  const [activeYearState, setActiveYearState] = useState<number | null>(null)
  const [stats, setStats] = useState<Stats | null>(null)
  const [newYear, setNewYear] = useState('')
  const [changingYear, setChangingYear] = useState(false)
  const [showAddYear, setShowAddYear] = useState(false)
  const [entriesOpen, setEntriesOpen] = useState(false)
  const [togglingEntries, setTogglingEntries] = useState(false)
  const [contestLive, setContestLive] = useState(false)
  const [togglingContest, setTogglingContest] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    checkAdminAndLoad()
  }, [])

  async function checkAdminAndLoad() {
    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      navigate('/login')
      return
    }

    const { data: profile } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', user.id)
      .single()

    if (!profile?.is_admin) {
      navigate('/dashboard')
      return
    }

    setIsAdmin(true)
    await loadData()
    setLoading(false)
  }

  async function loadData() {
    const [yearsData, currentYear] = await Promise.all([
      getAllYears(),
      getActiveYear()
    ])

    setYears(yearsData)
    setActiveYearState(currentYear)

    // Load stats and entries_open status for active year
    if (currentYear) {
      const [entriesResult, teamsResult, gamesResult, yearResult] = await Promise.all([
        supabase.from('entries').select('id, is_paid', { count: 'exact' }).eq('year', currentYear),
        supabase.from('tournament_teams').select('id', { count: 'exact' }).eq('year', currentYear),
        supabase.from('games').select('id', { count: 'exact' }).eq('year', currentYear).eq('is_complete', true),
        supabase.from('years').select('entries_open, contest_live').eq('year', currentYear).single()
      ])

      const entries = entriesResult.data || []
      setStats({
        totalEntries: entriesResult.count || 0,
        paidEntries: entries.filter((e: any) => e.is_paid === true).length,
        teamsCount: teamsResult.count || 0,
        gamesCompleted: gamesResult.count || 0
      })

      setEntriesOpen(yearResult.data?.entries_open || false)
      setContestLive(yearResult.data?.contest_live || false)
    }
  }

  async function handleSetActiveYear(year: number) {
    setChangingYear(true)
    await setActiveYear(year)
    await loadData()
    setChangingYear(false)
  }

  async function handleCreateYear() {
    const yearNum = parseInt(newYear)
    if (yearNum >= 2024 && yearNum <= 2100) {
      await createYear(yearNum)
      setNewYear('')
      setShowAddYear(false)
      await loadData()
    }
  }

  async function handleToggleEntriesOpen() {
    if (!activeYearState) return
    setTogglingEntries(true)
    
    const { error } = await supabase
      .from('years')
      .update({ entries_open: !entriesOpen })
      .eq('year', activeYearState)

    if (!error) {
      setEntriesOpen(!entriesOpen)
    }
    setTogglingEntries(false)
  }

  async function handleToggleContestLive() {
    if (!activeYearState) return
    setTogglingContest(true)
    
    const { error } = await supabase
      .from('years')
      .update({ contest_live: !contestLive })
      .eq('year', activeYearState)

    if (!error) {
      setContestLive(!contestLive)
    }
    setTogglingContest(false)
  }

  if (loading || !isAdmin) {
    return <div className="flex items-center justify-center min-h-[60vh] text-gray-500">Checking permissions...</div>
  }

  const adminSections = [
    {
      title: "Payment Manager",
      description: "Manage entry payment status",
      path: "/admin/payments",
      icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>,
      color: "bg-green-500"
    },
    {
      title: "Payout Config",
      description: "Entry fee & prize distribution",
      path: "/admin/payouts",
      icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
      color: "bg-amber-500"
    },
    {
      title: "Teams Setup",
      description: "Assign tournament teams",
      path: "/admin/years",
      icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/></svg>,
      color: "bg-blue-500"
    },
    {
      title: "Bracket Manager",
      description: "Enter game results",
      path: "/admin/bracket",
      icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2"/><line x1="12" y1="22" x2="12" y2="15.5"/><polyline points="22 8.5 12 15.5 2 8.5"/></svg>,
      color: "bg-purple-500"
    },
    {
      title: "Scoring Config",
      description: "Round point multipliers",
      path: "/admin/scoring",
      icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20V10"/><path d="M18 20V4"/><path d="M6 20v-4"/></svg>,
      color: "bg-orange-500"
    },
    {
      title: "Schools Database",
      description: "Add & edit team names",
      path: "/admin/schools",
      icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
      color: "bg-teal-500"
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#1e3a5f]">Admin Dashboard</h1>
        <p className="text-gray-500">Manage Wright Picks settings and data</p>
      </div>

      {/* Active Year Control - Simple inline */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-sm text-gray-500">Tournament Year:</span>
        {showAddYear ? (
          <div className="flex items-center gap-1">
            <input
              type="number"
              value={newYear}
              onChange={(e) => setNewYear(e.target.value)}
              placeholder="2025"
              className="w-20 px-2 py-1.5 rounded-lg text-sm text-center border border-gray-200"
              autoFocus
            />
            <button
              onClick={handleCreateYear}
              disabled={!newYear}
              className="px-3 py-1.5 rounded-lg text-sm bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            >
              Add
            </button>
            <button
              onClick={() => { setShowAddYear(false); setNewYear('') }}
              className="px-2 py-1.5 rounded-lg text-sm text-gray-500 hover:bg-gray-100"
            >
              ✕
            </button>
          </div>
        ) : (
          <select
            value={activeYearState || ''}
            onChange={(e) => {
              if (e.target.value === '__add_new__') {
                setShowAddYear(true)
              } else {
                handleSetActiveYear(parseInt(e.target.value))
              }
            }}
            disabled={changingYear}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-white text-[#1e3a5f] border border-gray-200 cursor-pointer"
          >
            {years.map(y => (
              <option key={y.year} value={y.year}>{y.year}</option>
            ))}
            <option disabled>───────</option>
            <option value="__add_new__">+ Add new year...</option>
          </select>
        )}
      </div>

      {/* Entries Open Toggle */}
      <Card className="bg-white border-0 shadow-sm">
        <CardContent className="py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#1e3a5f]">Entry Submissions</div>
              <div className="text-sm text-gray-500">
                {entriesOpen ? 'Open' : 'Closed'}
              </div>
            </div>
            <button
              onClick={handleToggleEntriesOpen}
              disabled={togglingEntries}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors shrink-0 ${
                entriesOpen ? 'bg-green-500' : 'bg-gray-300'
              } ${togglingEntries ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                  entriesOpen ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Contest Live Toggle */}
      <Card className="bg-white border-0 shadow-sm">
        <CardContent className="py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#1e3a5f]">Contest Live</div>
              <div className="text-sm text-gray-500">
                {contestLive ? 'Visible to players' : 'Hidden'}
              </div>
            </div>
            <button
              onClick={handleToggleContestLive}
              disabled={togglingContest}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors shrink-0 ${
                contestLive ? 'bg-green-500' : 'bg-gray-300'
              } ${togglingContest ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                  contestLive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Quick Stats for Active Year */}
      {stats && (
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <div><span className="font-bold text-[#1e3a5f]">{stats.teamsCount}</span> <span className="text-gray-500">teams</span></div>
          <div><span className="font-bold text-[#1e3a5f]">{stats.totalEntries}</span> <span className="text-gray-500">entries</span></div>
          <div><span className="font-bold text-[#1e3a5f]">{stats.paidEntries}</span> <span className="text-gray-500">paid</span></div>
          <div><span className="font-bold text-[#1e3a5f]">{stats.gamesCompleted}</span> <span className="text-gray-500">games done</span></div>
        </div>
      )}

      {/* Admin Sections Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {adminSections.map((section) => (
          <Link key={section.path} to={section.path}>
            <Card className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer h-full">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${section.color} text-white flex items-center justify-center`}>
                    {section.icon}
                  </div>
                  <div>
                    <CardTitle className="text-lg text-[#1e3a5f]">{section.title}</CardTitle>
                    <CardDescription className="text-gray-500 text-sm">
                      {section.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  )
}
